import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import * as icon from "@coreui/icons";
import { useNavigate } from 'react-router-dom'
import { useParams } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-user/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'

// helpers
import Axios from '../../helpers/axios'
import { singleCountriesOptionsSelect, singleGenderOptionSelect, singleRolesOptionsSelect } from '../../helpers/format-options'
import { capitalizeFirstLetter } from 'src/helpers/text-format';
import useToastMessage from 'src/helpers/toast-message'

const EditUser = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess } = useToastMessage();
  const params = useParams()
  const { id } = params

  const isLoading = useSelector((state) => state.isLoading)
  const user = JSON.parse(localStorage.getItem('user'))

  const [userDetail, setUserDetail] = useState('')

  const [countryOptions, setCountryOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [country, setCountry] = useState({})

  const [gender, setGender] = useState(null)
  const [role, setRole] = useState(null)

  const [showModalConfirm, setShowModalConfirm] = useState({ show: false, type: '', text: '' })

  const [isShowRoleOption, setIsShowRoleOption] = useState(false)

  const formik = useFormik({
    initialValues: {
      user_first_name: userDetail?.profile?.first_name,
      user_last_name: userDetail?.profile?.last_name,
      phone_number: userDetail?.profile?.phone_number,
      user_username: userDetail?.username,
      country_id: userDetail?.profile?.country?.id,
      user_gender: userDetail?.profile?.gender || '',
      is_active: userDetail.is_active,
      user_email: userDetail?.email,
      user_role_id: userDetail?.roles?.length > 0 ? userDetail?.roles[0]?.id : ''
    },
    validationSchema: Yup.object().shape({
      user_first_name: Yup.string()
        .required("Please enter first name."),
      user_last_name: Yup.string()
        .required("Please enter last name."),
      user_email: Yup.string().required("Please enter email address."),
      user_role_id: Yup.string().required("Please enter role."),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchAllCountries()
    fetchDetailUser()
    fetchAllRoles()
  }, [id]);

  const fetchDetailUser = () => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "get",
      url: `/api/sso/v1/users/id/${id}`,
    })
      .then(({ data }) => {
        if (data) {
          setUserDetail(data)
          if (data?.profile?.country) {
            setCountry(JSON.stringify({
              id: data?.profile?.country?.id,
              name: data?.profile?.country?.name,
              icon: icon[`cif${data?.profile?.country?.country_code}`],
              phone_code: data?.profile?.country?.phone_code
            }))
          }
          if (data?.profile?.gender) {
            setGender({
              label: capitalizeFirstLetter(data?.profile?.gender),
              value: data?.profile?.gender
            })
          }
          if (data?.roles?.length > 0) {
            setRole({
              label: capitalizeFirstLetter(data?.roles[0]?.title),
              value: data?.roles[0]?.id
            })

            if (user?.role_id !== data?.roles[0]?.id) {
              setIsShowRoleOption(true)
            }
          }
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries",
    })
      .then(({ data }) => {
        setCountryOptions(singleCountriesOptionsSelect(data))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const fetchAllRoles = () => {
    Axios({
      method: "get",
      url: "/api/sso/v1/users/roles",
    })
      .then(({ data }) => {
        setRoleOptions(singleRolesOptionsSelect(data))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const handleInputValue = (values) => {
    let data = {
      first_name: values.user_first_name,
      last_name: values.user_last_name,
      phone_number: values.phone_number || null,
      country_id: values.country_id || null,
      is_active: values.is_active,
      gender: values.user_gender || null,
      email: values.user_email,
      role_id: values.role_id
    }

    return data
  }

  const handleInputValueProfile = (values) => {
    let data = {
      first_name: values.user_first_name,
      last_name: values.user_last_name,
      phone_number: values.phone_number || null,
      country_id: values.country_id || null,
      gender: values.user_gender || null,
      user_id: id,
      role_id: values.role_id
    }

    return data
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "put",
      url: `/api/sso/v1/users/update/profile?user_id=${id}`,
      data: payload
    })
      .then(({ data }) => {
        if (data) {
          showSuccess('User has been successfully updated')
          navigate(`/user/details/${id}`)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleCreateProfile = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/sso/v1/users/profile/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data) {
          let payloadUpdateUser = {
            ...payload,
            email: formik.values.user_email,
            username: formik.values.user_username,
            is_active: formik.values.is_active
          }
          handlePost(payloadUpdateUser)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    if (userDetail?.profile) {
      const inputPayload = handleInputValue(values)
      handlePost(inputPayload)
    } else {
      const inputPayload = handleInputValueProfile(values)
      handleCreateProfile(inputPayload)
    }
  }

  const handleSubmit = () => {
    if (showModalConfirm.type === 'user') {
      formik.handleSubmit()
    }

    setShowModalConfirm({ show: false, type: '', text: '' })
  }

  const handleSelectCountry = (data) => {
    formik.setValues((values) => ({
      ...values,
      country_id: data ? JSON.parse(data?.value).id : ''
    }), false)

    setCountry(data? data?.value : null)
  }

  const handleSelectGender = (data) => {
    formik.setValues((values) => ({
      ...values,
      user_gender: data ? data?.value : ''
    }), false)

    setGender(data ? data : null)
  }

  const handleSelectRole = (data) => {
    formik.setValues((values) => ({
      ...values,
      role_id: data ? data?.value : ''
    }), false)

    setRole(data ? data : null)
  }

  return (
    <>
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText={showModalConfirm.text}
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Edit User</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            countryOptions={countryOptions}
            setCountry={setCountry}
            country={country}
            handleSelectCountry={handleSelectCountry}
            genderOptions={singleGenderOptionSelect()}
            gender={gender}
            handleSelectGender={handleSelectGender}
            roleOptions={roleOptions}
            role={role}
            handleSelectRole={handleSelectRole}
            isShowRole={isShowRoleOption}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate(`/user/details/${id}`)}
            onSave={() => setShowModalConfirm({ show: true, type: 'user', text: "Are you sure want to edit this user?" })}
          />
        }
      />
    </>
  )
}

export default EditUser